import type { SuperHubDocumentData } from '.';
import type { ReadChangelogType } from '@readme/api-contracts/schemas/changelog';
import type { CustomPageReadType } from '@readme/api-contracts/schemas/custompage';
import type { ReadGuideType } from '@readme/api/mappings/page/guide/types';
import type { ReadReferenceType } from '@readme/api/mappings/page/reference/types';
import type { SuperHubRouteParams } from '@routes/SuperHub/types';

import { projectStore } from '../../Project';

/**
 * Empty guide page to be used when creating a new page.
 */
function createDefaultGuidePage(): Partial<ReadGuideType['data']> {
  const defaultPageVisibility = projectStore.getState().data?.pages?.default_visibility || 'public';
  return {
    allow_crawlers: 'enabled',
    content: {
      body: null,
      excerpt: null,
      link: {
        new_tab: null,
        url: null,
      },
      next: {
        description: null,
        pages: [],
      },
    },
    metadata: {
      description: null,
      image: {
        uri: null,
        url: null,
      },
      keywords: null,
      title: null,
      x_import: null,
    },
    privacy: { view: defaultPageVisibility },
    renderable: {
      status: true,
    },
    slug: '',
    state: 'current',
    title: '',
    type: 'basic',
  };
}

/**
 * Empty reference page to be used when creating a new page.
 */
function createDefaultReferencePage(): Partial<ReadReferenceType['data']> {
  return {
    ...createDefaultGuidePage(),
    api: {
      method: 'get',
      path: '',
      schema: null,
      stats: {
        additional_properties: false,
        callbacks: false,
        circular_references: false,
        common_parameters: false,
        discriminators: false,
        links: false,
        polymorphism: false,
        references: false,
        server_variables: false,
        style: false,
        webhooks: false,
        xml_requests: false,
        xml_responses: false,
        xml_schemas: false,
      },
      source: null,
      uri: null,
      validation: {
        status: 'valid',
        reason: null,
      },
    },
  };
}

/**
 * Empty custom page to be used when creating a new page.
 */
function createDefaultCustomPage(): Partial<CustomPageReadType['data']> {
  const defaultPageVisibility = projectStore.getState().data?.pages?.default_visibility || 'public';
  return {
    appearance: {
      fullscreen: false,
    },
    content: {
      body: null,
      type: 'markdown',
    },
    metadata: {
      description: null,
      image: {
        uri: null,
        url: null,
      },
      keywords: null,
      title: null,
    },
    privacy: { view: defaultPageVisibility },
    slug: '',
    title: '',
  };
}

/**
 * Empty changelog to be used when creating a new post.
 */
function createDefaultChangelog(user: { id: string | null; name: string | null }): Partial<ReadChangelogType['data']> {
  return {
    author: {
      id: user.id,
      name: user.name,
    },
    created_at: new Date().toISOString(),
    content: {
      body: null,
      html: null,
    },
    metadata: {
      description: null,
      image: {
        uri: null,
        url: null,
      },
      keywords: null,
      title: null,
    },
    privacy: {
      // Irregardless of a projects default page visibility changelogs should always have a default
      // privacy of private until it's published. https://linear.app/readme-io/issue/CX-2720
      view: 'anyone_with_link',
    },
    slug: '',
    title: '',
    type: 'none',
  };
}

/**
 * Returns the empty default document based on the provided route section that
 * represents the initial state when creating new documents.
 */
export function getDefaultDocument(
  routeSection: SuperHubRouteParams['section'] | null,
  user: { id: string | null; name: string | null },
) {
  if (!routeSection) return null;

  /**
   * Hash of default documents by route section to use as the form's empty state
   * when creating new pages.
   */
  const defaultDocumentMap = {
    api_definitions: null,
    changelog: createDefaultChangelog(user),
    custom_components: null,
    discuss: null,
    docs: createDefaultGuidePage(),
    home: null,
    page: createDefaultCustomPage(),
    recipes: null,
    reference: createDefaultReferencePage(),
    reusable_content: null,
  } satisfies Record<NonNullable<SuperHubRouteParams['section']>, Partial<SuperHubDocumentData> | null>;

  return defaultDocumentMap[routeSection] as SuperHubDocumentData;
}
